/**
 * Copyright (c) 2018-2024 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author David Sehnal <david.sehnal@gmail.com>
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 * @author Gianluca Tomasello <giagitom@gmail.com>
 */
import { GridLookup3D, Result } from '../../../../mol-math/geometry.js';
import { Vec3 } from '../../../../mol-math/linear-algebra.js';
import { OrderedSet } from '../../../../mol-data/int.js';
import { StructureElement } from '../element.js';
import { FibonacciHeap } from '../../../../mol-util/fibonacci-heap.js';
export var StructureResult;
(function (StructureResult) {
    function add(result, unit, index, distSq) {
        result.indices[result.count] = index;
        result.units[result.count] = unit;
        result.squaredDistances[result.count] = distSq;
        result.count++;
    }
    StructureResult.add = add;
    function create() {
        return { count: 0, indices: [], units: [], squaredDistances: [] };
    }
    StructureResult.create = create;
    function copy(out, result) {
        for (let i = 0; i < result.count; ++i) {
            out.indices[i] = result.indices[i];
            out.units[i] = result.units[i];
            out.squaredDistances[i] = result.squaredDistances[i];
        }
        out.count = result.count;
        return out;
    }
    StructureResult.copy = copy;
})(StructureResult || (StructureResult = {}));
export function StructureLookup3DResultContext() {
    return { result: StructureResult.create(), closeUnitsResult: Result.create(), unitGroupResult: Result.create() };
}
export class StructureLookup3D {
    findUnitIndices(x, y, z, radius) {
        return this.unitLookup.find(x, y, z, radius);
    }
    find(x, y, z, radius, ctx) {
        return this._find(x, y, z, radius, ctx !== null && ctx !== void 0 ? ctx : this.findContext);
    }
    _find(x, y, z, radius, ctx) {
        Result.reset(ctx.result);
        const { units } = this.structure;
        const closeUnits = this.unitLookup.find(x, y, z, radius, ctx.closeUnitsResult);
        if (closeUnits.count === 0)
            return ctx.result;
        for (let t = 0, _t = closeUnits.count; t < _t; t++) {
            const unit = units[closeUnits.indices[t]];
            Vec3.set(this.pivot, x, y, z);
            if (!unit.conformation.operator.isIdentity) {
                Vec3.transformMat4(this.pivot, this.pivot, unit.conformation.operator.inverse);
            }
            const unitLookup = unit.lookup3d;
            const groupResult = unitLookup.find(this.pivot[0], this.pivot[1], this.pivot[2], radius, ctx.unitGroupResult);
            for (let j = 0, _j = groupResult.count; j < _j; j++) {
                StructureResult.add(ctx.result, unit, groupResult.indices[j], groupResult.squaredDistances[j]);
            }
        }
        return ctx.result;
    }
    nearest(x, y, z, k = 1, ctx) {
        return this._nearest(x, y, z, k, ctx !== null && ctx !== void 0 ? ctx : this.findContext);
    }
    _nearest(x, y, z, k, ctx) {
        const result = ctx.result, heap = this.heap;
        Result.reset(result);
        heap.clear();
        const { units } = this.structure;
        let elementsCount = 0;
        const closeUnits = this.unitLookup.nearest(x, y, z, units.length, (uid) => (elementsCount += units[uid].elements.length) >= k, ctx.closeUnitsResult); // sort units based on distance to the point
        if (closeUnits.count === 0)
            return result;
        let totalCount = 0, maxDistResult = -Number.MAX_VALUE;
        for (let t = 0, _t = closeUnits.count; t < _t; t++) {
            const unitSqDist = closeUnits.squaredDistances[t];
            if (totalCount >= k && maxDistResult < unitSqDist)
                break;
            Vec3.set(this.pivot, x, y, z);
            const unit = units[closeUnits.indices[t]];
            if (!unit.conformation.operator.isIdentity) {
                Vec3.transformMat4(this.pivot, this.pivot, unit.conformation.operator.inverse);
            }
            const unitLookup = unit.lookup3d;
            const groupResult = unitLookup.nearest(this.pivot[0], this.pivot[1], this.pivot[2], k, void 0, ctx.unitGroupResult);
            if (groupResult.count === 0)
                continue;
            totalCount += groupResult.count;
            maxDistResult = Math.max(maxDistResult, groupResult.squaredDistances[groupResult.count - 1]);
            for (let j = 0, _j = groupResult.count; j < _j; j++) {
                heap.insert(groupResult.squaredDistances[j], { index: groupResult.indices[j], unit: unit });
            }
        }
        if (k === 1) {
            const node = heap.findMinimum();
            if (node) {
                const { key: squaredDistance } = node;
                const { unit, index } = node.value;
                StructureResult.add(result, unit, index, squaredDistance);
            }
        }
        else {
            while (!heap.isEmpty() && result.count < k) {
                const node = heap.extractMinimum();
                const { key: squaredDistance } = node;
                const { unit, index } = node.value;
                StructureResult.add(result, unit, index, squaredDistance);
            }
        }
        return result;
    }
    findIntoBuilder(x, y, z, radius, builder) {
        const { units } = this.structure;
        const closeUnits = this.unitLookup.find(x, y, z, radius);
        if (closeUnits.count === 0)
            return;
        for (let t = 0, _t = closeUnits.count; t < _t; t++) {
            const unit = units[closeUnits.indices[t]];
            Vec3.set(this.pivot, x, y, z);
            if (!unit.conformation.operator.isIdentity) {
                Vec3.transformMat4(this.pivot, this.pivot, unit.conformation.operator.inverse);
            }
            const unitLookup = unit.lookup3d;
            const groupResult = unitLookup.find(this.pivot[0], this.pivot[1], this.pivot[2], radius);
            if (groupResult.count === 0)
                continue;
            const elements = unit.elements;
            builder.beginUnit(unit.id);
            for (let j = 0, _j = groupResult.count; j < _j; j++) {
                builder.addElement(elements[groupResult.indices[j]]);
            }
            builder.commitUnit();
        }
    }
    findIntoBuilderIf(x, y, z, radius, builder, test) {
        const { units } = this.structure;
        const closeUnits = this.unitLookup.find(x, y, z, radius);
        if (closeUnits.count === 0)
            return;
        const loc = StructureElement.Location.create(this.structure);
        for (let t = 0, _t = closeUnits.count; t < _t; t++) {
            const unit = units[closeUnits.indices[t]];
            Vec3.set(this.pivot, x, y, z);
            if (!unit.conformation.operator.isIdentity) {
                Vec3.transformMat4(this.pivot, this.pivot, unit.conformation.operator.inverse);
            }
            const unitLookup = unit.lookup3d;
            const groupResult = unitLookup.find(this.pivot[0], this.pivot[1], this.pivot[2], radius);
            if (groupResult.count === 0)
                continue;
            const elements = unit.elements;
            loc.unit = unit;
            builder.beginUnit(unit.id);
            for (let j = 0, _j = groupResult.count; j < _j; j++) {
                loc.element = elements[groupResult.indices[j]];
                if (test(loc)) {
                    builder.addElement(loc.element);
                }
            }
            builder.commitUnit();
        }
    }
    findIntoBuilderWithRadius(x, y, z, pivotR, maxRadius, radius, eRadius, builder) {
        const { units } = this.structure;
        const closeUnits = this.unitLookup.find(x, y, z, radius);
        if (closeUnits.count === 0)
            return;
        const se = StructureElement.Location.create(this.structure);
        const queryRadius = pivotR + maxRadius + radius;
        for (let t = 0, _t = closeUnits.count; t < _t; t++) {
            const unit = units[closeUnits.indices[t]];
            Vec3.set(this.pivot, x, y, z);
            if (!unit.conformation.operator.isIdentity) {
                Vec3.transformMat4(this.pivot, this.pivot, unit.conformation.operator.inverse);
            }
            const unitLookup = unit.lookup3d;
            const groupResult = unitLookup.find(this.pivot[0], this.pivot[1], this.pivot[2], queryRadius);
            if (groupResult.count === 0)
                continue;
            const elements = unit.elements;
            se.unit = unit;
            builder.beginUnit(unit.id);
            for (let j = 0, _j = groupResult.count; j < _j; j++) {
                se.element = elements[groupResult.indices[j]];
                const rr = eRadius(se);
                if (Math.sqrt(groupResult.squaredDistances[j]) - pivotR - rr > radius)
                    continue;
                builder.addElement(elements[groupResult.indices[j]]);
            }
            builder.commitUnit();
        }
    }
    check(x, y, z, radius) {
        const { units } = this.structure;
        const closeUnits = this.unitLookup.find(x, y, z, radius);
        if (closeUnits.count === 0)
            return false;
        for (let t = 0, _t = closeUnits.count; t < _t; t++) {
            const unit = units[closeUnits.indices[t]];
            Vec3.set(this.pivot, x, y, z);
            if (!unit.conformation.operator.isIdentity) {
                Vec3.transformMat4(this.pivot, this.pivot, unit.conformation.operator.inverse);
            }
            const groupLookup = unit.lookup3d;
            if (groupLookup.check(this.pivot[0], this.pivot[1], this.pivot[2], radius))
                return true;
        }
        return false;
    }
    approxNearest(x, y, z, radius, ctx) {
        return this._approxNearest(x, y, z, radius, ctx !== null && ctx !== void 0 ? ctx : this.findContext);
    }
    _approxNearest(x, y, z, radius, ctx) {
        Result.reset(ctx.result);
        const { units } = this.structure;
        const closeUnits = this.unitLookup.find(x, y, z, radius, ctx.closeUnitsResult);
        if (closeUnits.count === 0)
            return ctx.result;
        let minDistSq = Number.MAX_VALUE;
        for (let t = 0, _t = closeUnits.count; t < _t; t++) {
            const unit = units[closeUnits.indices[t]];
            Vec3.set(this.pivot, x, y, z);
            if (!unit.conformation.operator.isIdentity) {
                Vec3.transformMat4(this.pivot, this.pivot, unit.conformation.operator.inverse);
            }
            const unitLookup = unit.lookup3d;
            const groupResult = unitLookup.approxNearest(this.pivot[0], this.pivot[1], this.pivot[2], radius, ctx.unitGroupResult);
            for (let j = 0, _j = groupResult.count; j < _j; j++) {
                if (groupResult.squaredDistances[j] < minDistSq) {
                    StructureResult.add(ctx.result, unit, groupResult.indices[j], groupResult.squaredDistances[j]);
                    minDistSq = groupResult.squaredDistances[j];
                }
            }
        }
        return ctx.result;
    }
    get boundary() {
        return this.structure.boundary;
    }
    constructor(structure) {
        this.structure = structure;
        this.pivot = Vec3();
        this.heap = new FibonacciHeap();
        this.findContext = StructureLookup3DResultContext();
        const { units, boundary } = structure;
        const unitCount = units.length;
        const xs = new Float32Array(unitCount);
        const ys = new Float32Array(unitCount);
        const zs = new Float32Array(unitCount);
        const radius = new Float32Array(unitCount);
        const center = Vec3();
        for (let i = 0; i < unitCount; i++) {
            const unit = units[i];
            const s = unit.boundary.sphere;
            Vec3.transformMat4(center, s.center, unit.conformation.operator.matrix);
            xs[i] = center[0];
            ys[i] = center[1];
            zs[i] = center[2];
            radius[i] = s.radius;
        }
        const position = { x: xs, y: ys, z: zs, radius, indices: OrderedSet.ofBounds(0, unitCount) };
        this.unitLookup = GridLookup3D(position, boundary);
    }
}
